import { useCallback, useEffect, useRef, useState } from "react";
import { ApiRequestError } from "@mmm/contracts";
import { ApiTransportError, AuthRequiredError } from "./client.js";
import { invalidationGeneration, subscribeToInvalidation } from "./invalidate.js";

export interface QueryState<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  refreshing: boolean;
  waiting: boolean;
  /**
   * Re-runs the query from attempt 0 — for the "retries exhausted, `error`
   * is set" terminal state (see `ErrorState`'s Retry button). A stable
   * function identity across renders; calling it before the first fetch
   * lands, or after the owning component has unmounted, is a harmless no-op.
   */
  retry: () => void;
}

interface UseQueryOptions {
  /** Skip fetching (and clear `data`) while false — for queries that need a param not yet available. */
  enabled?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type QueryFn<T> = (args?: any) => Promise<T>;

// Session-scoped stale-while-revalidate cache. Navigating back to a screen
// renders its last-known data instantly (no blank-then-pop) while a fresh
// read runs in the background; command invalidation refreshes it silently.
const fnIds = new WeakMap<QueryFn<unknown>, number>();
let nextFnId = 1;
const cache = new Map<string, unknown>();

/**
 * Reads of the same query that are already under way, so simultaneous callers
 * share one request instead of each issuing their own.
 *
 * Several components asking the same question at the same moment is normal --
 * the sidebar and the settings modal both want the session, and every mounted
 * query re-reads on invalidation. Without this, each of them hit the network
 * separately: a single command produced one request per subscriber, and the
 * dev server log showed getSession fetched dozens of times over.
 *
 * Keyed by invalidation round as well as by query, so this only ever joins
 * reads that began for the same reason. A read already in flight when the data
 * changed belongs to the previous round and cannot answer for the new one --
 * joining it would serve exactly the stale data the invalidation was raised to
 * replace.
 */
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Reads the key, sharing and caching under one rule: a result may only be
 * written to the cache if nothing has been invalidated since the read began.
 *
 * The cache outlives every component, so "is this read still wanted?" cannot be
 * asked of the component that started it. A screen could begin loading, unmount
 * (unsubscribing, so nothing would ever mark its read superseded), a command
 * could then invalidate and a fresh read land -- and the abandoned request,
 * resolving last, would overwrite the cache with data from before the mutation.
 * The next visit to that screen rendered the stale value straight from cache.
 *
 * Comparing generations answers it without needing an owner: a read that began
 * before the data changed cannot describe the data now, whoever asked for it.
 */
function fetchShared<T>(key: string, queryFn: QueryFn<T>, args: unknown): Promise<T> {
  const generation = invalidationGeneration();
  const roundKey = `${key}@${generation}`;
  const existing = inFlight.get(roundKey) as Promise<T> | undefined;
  if (existing) return existing;

  const pending = queryFn(args)
    .then((data: T) => {
      if (invalidationGeneration() === generation) cache.set(key, data);
      return data;
    })
    // Settled reads are dropped, so a failure is retried rather than replayed.
    .finally(() => {
      if (inFlight.get(roundKey) === pending) inFlight.delete(roundKey);
    });
  inFlight.set(roundKey, pending);
  return pending;
}

const NON_TRANSIENT_RETRIES = 3;
const RETRY_DELAYS_MS = [400, 900, 1600, 3000, 5000, 8000, 13000, 15000];

function keyFor(queryFn: QueryFn<unknown>, argsKey: string): string {
  let id = fnIds.get(queryFn);
  if (id === undefined) {
    id = nextFnId++;
    fnIds.set(queryFn, id);
  }
  return `${id}:${argsKey}`;
}

/**
 * Fetch-and-cache outside React — e.g. the boot splash pre-warming queries.
 *
 * Goes through the same shared read as the hook, so it joins a read already
 * running for the same key and is held to the same rule about what may be
 * written: priming used to reach past both and could commit a result the rest
 * of the app had already superseded.
 */
export async function primeQuery<T>(queryFn: QueryFn<T>, args?: unknown): Promise<void> {
  const key = keyFor(queryFn, JSON.stringify(args ?? null));
  await fetchShared(key, queryFn, args);
}

export function isRetriableQueryError(error: unknown): boolean {
  // Not transient, despite extending ApiTransportError with no status. Retrying
  // just re-runs the same failed silent token acquisition, and that is what
  // turned an expired session into an endless splash: every attempt opened
  // another hidden Entra iframe that could never succeed. AuthProvider surfaces
  // the sign-in screen for this instead, which one click recovers from.
  if (error instanceof AuthRequiredError) return false;
  if (error instanceof ApiRequestError) {
    return error.code === "unexpected" || error.code === "aiUnavailable" || error.code === "rateLimited";
  }
  if (error instanceof ApiTransportError) {
    return error.status === undefined || error.status === 408 || error.status === 429 || error.status >= 500;
  }
  return true;
}

/**
 * Retries are bounded. This gates the boot splash, so an unbounded loop here
 * means an app that never finishes loading and never says why -- which is
 * exactly what a genuinely stuck dependency produced. Giving up lets the
 * caller show something actionable; the splash treats a rejection as "ready"
 * and reveals the screen underneath.
 */
const WAIT_FOR_QUERY_MAX_RETRIES = 8;

export async function waitForQuery<T>(queryFn: QueryFn<T>, args?: unknown): Promise<void> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await primeQuery(queryFn, args);
      return;
    } catch (error) {
      if (!isRetriableQueryError(error) || attempt >= WAIT_FOR_QUERY_MAX_RETRIES) throw error;
      await delayForAttempt(attempt);
    }
  }
}

function delayForAttempt(attempt: number): Promise<void> {
  const ms = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface KeyedState<T> extends Omit<QueryState<T>, "retry"> {
  key: string;
}

/**
 * Serves cached data for the key immediately (loading only on a cache miss),
 * then revalidates: on mount, whenever `args` changes, and on every command
 * invalidation. Stale data stays visible during revalidation; a failed read
 * drops the cache entry and surfaces the error.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useQuery<T>(queryFn: QueryFn<T>, args?: any, options: UseQueryOptions = {}): QueryState<T> {
  const enabled = options.enabled ?? true;
  const argsKey = JSON.stringify(args ?? null);
  const key = keyFor(queryFn, argsKey);

  const [state, setState] = useState<KeyedState<T>>(() => {
    const cached = cache.get(key) as T | undefined;
    return {
      key,
      data: cached,
      error: undefined,
      loading: enabled && cached === undefined,
      refreshing: enabled && cached !== undefined,
      waiting: false,
    };
  });

  // Stable ref-based retry trigger: the effect below reassigns `runRef` to
  // the current mount's `run(0)` every time it (re)fires (on mount, on `key`
  // change, on invalidation); `retry` itself never changes identity, so it
  // can be handed to a child (e.g. ErrorState's onRetry) without that child
  // needing to resubscribe. The cleanup below clears it back to a no-op, so
  // a retry click racing an unmount/key-change is inert rather than
  // reaching into a stale closure.
  const runRef = useRef<() => void>(() => {});
  const retry = useCallback(() => runRef.current(), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    // Each read this effect starts gets a number, and only the newest one is
    // allowed to land. An invalidation starts a fresh read without waiting for
    // whatever was already in flight, so the older one could resolve second and
    // write the very data the invalidation was raised to replace -- into the
    // cache as well as the screen, which outlived the component that fetched it.
    let latestRun = 0;

    const run = (attempt = 0) => {
      const runId = (latestRun += 1);
      const superseded = () => runId !== latestRun;
      // Retries belong to the chain that scheduled them. An invalidation or a
      // manual retry starts a new chain, and the old one's pending timer had to
      // die with it: left running, it fired later, took over as the newest read,
      // and could carry a flaky response into a fresh retry chain that ended by
      // deleting a valid cache entry and replacing a working screen with an
      // error -- long after the read it was retrying stopped mattering.
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = undefined;
      const current = cache.get(key) as T | undefined;
      setState({
        key,
        data: current,
        error: undefined,
        loading: current === undefined,
        refreshing: current !== undefined,
        waiting: attempt > 0,
      });

      fetchShared(key, queryFn, args).then(
        (data: T) => {
          // The cache is written by the read itself, which is the only place
          // that knows whether the data has been superseded app-wide rather
          // than merely within this component.
          if (superseded()) return;
          if (!cancelled) {
            setState({ key, data, error: undefined, loading: false, refreshing: false, waiting: false });
          }
        },
        (error: unknown) => {
          if (cancelled || superseded()) return;
          const retriable = isRetriableQueryError(error);
          if (retriable || attempt < NON_TRANSIENT_RETRIES) {
            const nextAttempt = attempt + 1;
            const cached = cache.get(key) as T | undefined;
            setState({
              key,
              data: cached,
              error: undefined,
              loading: cached === undefined,
              refreshing: cached !== undefined,
              waiting: true,
            });
            retryTimer = setTimeout(
              () => run(nextAttempt),
              RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]
            );
            return;
          }
          cache.delete(key);
          setState({ key, data: undefined, error, loading: false, refreshing: false, waiting: false });
        }
      );
    };

    runRef.current = () => run(0);
    run();
    const unsubscribe = subscribeToInvalidation(() => run());

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      unsubscribe();
      runRef.current = () => {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  if (!enabled) return { data: undefined, error: undefined, loading: false, refreshing: false, waiting: false, retry };
  if (state.key !== key) {
    // args changed this render — serve the new key's cached value until the
    // effect's fetch lands (avoids flashing the previous key's data).
    const cached = cache.get(key) as T | undefined;
    return {
      data: cached,
      error: undefined,
      loading: cached === undefined,
      refreshing: cached !== undefined,
      waiting: false,
      retry,
    };
  }
  return { ...state, retry };
}
